/**
 * Scroll reveal for the home page (Titan-7 / Stiletto style).
 * - Individual blocks (cards, headings, icons...) fade in and slide up
 *   with a staggered delay, wave-like, as they enter the viewport.
 * - Images settle from a slight zoom (scale 1.12 -> 1) while fading in.
 * Hidden state is applied by JS only, so if this script fails the page
 * renders normally with everything visible.
 */
(function () {
  const main = document.querySelector('#MainContent');
  if (!main || main.dataset.template !== 'index') return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion || !('IntersectionObserver' in window)) return;

  const ITEM_SELECTOR = [
    '.resource-list__item',
    '.collection-card',
    '.icon-block',
    '.group-block',
    '.text-block',
    '.image-block',
    '.media-block',
    '.accordion',
    '.featured-blog-posts-card',
    '.hotspots-container',
    '.vehicle-gallery__item',
  ].join(', ');

  const STAGGER_MS = 90;
  const MAX_STAGGER_STEPS = 7;
  const ITEM_DURATION_MS = 600;
  const IMAGE_DURATION_MS = 1100;

  const sections = Array.from(main.querySelectorAll(':scope > .shopify-section'));
  // Skip the first section (hero) — it should be visible immediately.
  const candidateSections = sections.slice(1);
  if (candidateSections.length === 0) return;

  /** Collect top-level animatable items of a section (no nested duplicates). */
  function collectItems(section) {
    // Marquee scrolls continuously — animating its children would glitch.
    if (section.id.includes('marquee')) return [section];

    const found = Array.from(section.querySelectorAll(ITEM_SELECTOR));
    const topLevel = found.filter((el) => {
      const ancestor = el.parentElement && el.parentElement.closest(ITEM_SELECTOR);
      return !ancestor || !section.contains(ancestor);
    });
    return topLevel.length > 0 ? topLevel : [section];
  }

  function prepareImages(item) {
    for (const img of item.querySelectorAll('img')) {
      const wrapper = img.parentElement;
      if (wrapper) wrapper.classList.add('scroll-reveal-zoom');
    }
  }

  function cleanup(item, delayMs) {
    const total = delayMs + Math.max(ITEM_DURATION_MS, IMAGE_DURATION_MS) + 150;
    setTimeout(() => {
      item.classList.remove('scroll-reveal-item', 'is-visible');
      item.style.removeProperty('--reveal-delay');
      for (const wrapper of item.querySelectorAll('.scroll-reveal-zoom')) {
        wrapper.classList.remove('scroll-reveal-zoom');
      }
      if (item.classList.contains('scroll-reveal-zoom')) {
        item.classList.remove('scroll-reveal-zoom');
      }
    }, total);
  }

  const observer = new IntersectionObserver(
    (entries) => {
      // Reveal the batch that entered together as a wave: sort by position,
      // stagger each one a bit more than the previous.
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => {
          const ra = a.boundingClientRect;
          const rb = b.boundingClientRect;
          return ra.top - rb.top || ra.left - rb.left;
        });

      visible.forEach((entry, index) => {
        const item = entry.target;
        const delayMs = Math.min(index, MAX_STAGGER_STEPS) * STAGGER_MS;
        item.style.setProperty('--reveal-delay', delayMs + 'ms');
        item.classList.add('is-visible');
        observer.unobserve(item);
        cleanup(item, delayMs);
      });
    },
    {
      // Trigger slightly before the item fully reaches the viewport bottom.
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.1,
    }
  );

  /**
   * Find items below the reveal threshold with minimal layout reads.
   * Measure each section once; only partially visible sections need per-item reads.
   */
  function collectBelowViewportItems(viewportThreshold) {
    const belowViewport = [];

    for (const section of candidateSections) {
      const items = collectItems(section);
      const sectionRect = section.getBoundingClientRect();

      if (sectionRect.top >= viewportThreshold) {
        belowViewport.push(...items);
        continue;
      }

      if (sectionRect.bottom <= viewportThreshold) continue;

      for (const item of items) {
        if (item.getBoundingClientRect().top >= viewportThreshold) {
          belowViewport.push(item);
        }
      }
    }

    return belowViewport;
  }

  function init() {
    const viewportThreshold = window.innerHeight * 0.9;
    const belowViewport = collectBelowViewportItems(viewportThreshold);

    for (const item of belowViewport) {
      prepareImages(item);
      item.classList.add('scroll-reveal-item');
      observer.observe(item);
    }
  }

  // Defer until the browser is idle so header/layout scripts finish first.
  // Fallback: next frame — avoids a long delay that could flash off-screen content.
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(init, { timeout: 500 });
  } else {
    requestAnimationFrame(init);
  }

  // Reset state after Shopify theme editor re-renders a section.
  document.addEventListener('shopify:section:load', (event) => {
    for (const el of event.target.querySelectorAll('.scroll-reveal-item, .scroll-reveal-zoom')) {
      el.classList.remove('scroll-reveal-item', 'is-visible', 'scroll-reveal-zoom');
      el.style.removeProperty('--reveal-delay');
    }
  });
})();
